import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, List, ListItem, ListItemAvatar, ListItemText, MenuItem,
  Paper, Select, Typography, Avatar, IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import PersonIcon from "@material-ui/icons/Person";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import ActivityService from "./Locality/Service/activityService";
import StudentService from "./Locality/Service/studentService";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const resolveSchoolId = (userDetails) => {
  const id = userDetails?.schooleId?._id || userDetails?.schooleId
    || userDetails?.schoolId?._id || userDetails?.schoolId;
  return id ? String(id) : "";
};

const transformActivityData = (data) => {
  const result = {};
  (data || []).forEach((item) => {
    if (["M", "WI", "WA", "P", "Future Plane"].includes(item._id)) {
      result[item._id] = {
        activities: item.activities || [],
        studentCount: item.studentCount || 0,
        students: item.students || [],
      };
    }
  });
  return result;
};

const buildConsolidatedData = (buckets) => {
  const map = new Map();

  const addFromBucket = (bucketKey, stage) => {
    const bucket = buckets && buckets[bucketKey];
    if (!bucket || !Array.isArray(bucket.students)) return;

    bucket.students.forEach((student) => {
      const fullName = `${(student.firstName || "").trim()} ${(student.lastName || "").trim()}`.trim() || "Unnamed Student";
      const sid = student.studentId ? String(student.studentId) : fullName;

      (student.activities || []).forEach((act) => {
        const activityId = act.activityId ? String(act.activityId) : "";
        const activityName = act.activityName || "Unnamed Activity";
        const key = activityId || activityName;

        if (!map.has(key)) {
          map.set(key, {
            activityId,
            activityName,
            strongStudents: new Map(),
            developingStudents: new Map(),
            needsAttentionStudents: new Map(),
          });
        }
        const entry = map.get(key);
        let target;
        if (stage === "strong") target = entry.strongStudents;
        else if (stage === "developing") target = entry.developingStudents;
        else target = entry.needsAttentionStudents;

        if (!target.has(sid)) {
          target.set(sid, fullName);
        }
      });
    });
  };

  addFromBucket("M", "strong");
  addFromBucket("WI", "strong");
  addFromBucket("WA", "developing");
  addFromBucket("P", "needs");

  return Array.from(map.values())
    .map((row) => ({
      activityId: row.activityId,
      activityName: row.activityName,
      strongStudents: Array.from(row.strongStudents.values()),
      developingStudents: Array.from(row.developingStudents.values()),
      needsAttentionStudents: Array.from(row.needsAttentionStudents.values()),
    }))
    .sort((a, b) => a.activityName.localeCompare(b.activityName));
};

const downloadConsolidatedCsv = (rows) => {
  if (!rows.length) return;
  const header = ["Activity", "Strong area students", "Developing area students", "Needs attention students"];
  const escape = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
  const lines = [header.map(escape).join(",")];
  rows.forEach((row) => {
    lines.push([
      escape(row.activityName),
      escape(row.strongStudents.join("; ")),
      escape(row.developingStudents.join("; ")),
      escape(row.needsAttentionStudents.join("; ")),
    ].join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "activity_consolidated_report.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const StudentCategoryCell = ({ students, classes }) => {
  if (!students || students.length === 0) {
    return (
      <td className={`${classes.reportBodyCell} ${classes.emptyDash}`}>-</td>
    );
  }

  return (
    <td className={classes.reportBodyCell}>
      <Typography className={classes.studentCount}>{students.length}</Typography>
      <Box component="ul" className={classes.studentList}>
        {students.map((name, idx) => (
          <Box component="li" key={`${name}-${idx}`} className={classes.studentNameItem}>
            {name}
          </Box>
        ))}
      </Box>
    </td>
  );
};

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(2.5),
    background: "#f8fafc",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  filtersCard: {
    borderRadius: 22,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2.5),
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  },
  filterLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#64748b",
    marginBottom: theme.spacing(0.75),
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  filterSelect: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f8fafc",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e2e8f0",
    },
  },
  searchButton: {
    borderRadius: 12,
    padding: theme.spacing(1.1, 2.5),
    textTransform: "none",
    fontWeight: 700,
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
    height: 40,
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    },
  },
  summaryCardsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2),
    },
  },
  summaryCard: {
    borderRadius: 18,
    padding: theme.spacing(2.25),
    height: "100%",
    border: "1px solid",
    boxSizing: "border-box",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    },
  },
  summaryHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    fontSize: "1rem",
    marginBottom: 12,
  },
  summaryCount: {
    marginLeft: "auto",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: "1.35rem",
    padding: "2px 12px",
    borderRadius: 10,
    lineHeight: 1.4,
  },
  summaryList: {
    margin: 0,
    paddingLeft: 18,
    maxHeight: 160,
    overflowY: "auto",
    "& li": {
      fontSize: "0.88rem",
      lineHeight: 1.7,
      color: "#475569",
    },
  },
  reportCard: {
    borderRadius: 24,
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      borderRadius: 18,
    },
  },
  reportHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    flexWrap: "wrap",
  },
  reportTitle: {
    fontWeight: 800,
    fontSize: "1.35rem",
    color: "#0f2d4d",
    letterSpacing: "-0.01em",
  },
  downloadBtn: {
    backgroundColor: "#0f2d4d",
    color: "#ffffff",
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    padding: theme.spacing(1, 2.25),
    boxShadow: "0 6px 16px rgba(15, 45, 77, 0.22)",
    "&:hover": {
      backgroundColor: "#0b2238",
    },
    "&.Mui-disabled": {
      backgroundColor: "#cbd5e1",
      color: "#ffffff",
    },
  },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
  },
  reportTable: {
    width: "100%",
    minWidth: 720,
    borderCollapse: "collapse",
    background: "#ffffff",
  },
  reportHeadCell: {
    background: "#f1f5f9",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.88rem",
    padding: theme.spacing(1.75, 2),
    textAlign: "left",
    borderBottom: "1px solid #e2e8f0",
    borderRight: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
    "&:last-child": {
      borderRight: "none",
    },
  },
  reportBodyCell: {
    padding: theme.spacing(2, 2),
    borderBottom: "1px solid #e2e8f0",
    borderRight: "1px solid #e2e8f0",
    verticalAlign: "top",
    "&:last-child": {
      borderRight: "none",
    },
  },
  activityNameCell: {
    color: "#334155",
    fontWeight: 600,
    fontSize: "0.95rem",
    minWidth: 180,
  },
  studentCount: {
    fontWeight: 800,
    fontSize: "1.6rem",
    color: "#0f172a",
    lineHeight: 1.1,
    marginBottom: theme.spacing(0.75),
  },
  studentList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  studentNameItem: {
    fontSize: "0.88rem",
    color: "#475569",
    lineHeight: 1.65,
    position: "relative",
    paddingLeft: theme.spacing(1.75),
    "&::before": {
      content: '"•"',
      position: "absolute",
      left: 0,
      color: "#94a3b8",
      fontWeight: 700,
    },
  },
  emptyDash: {
    textAlign: "center",
    color: "#94a3b8",
    fontWeight: 600,
    fontSize: "1rem",
    verticalAlign: "middle",
  },
  emptyRow: {
    textAlign: "center",
    padding: theme.spacing(5, 2),
    color: "#64748b",
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  loadingBox: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#2563eb",
    fontWeight: 700,
  },
  dialogPaper: {
    borderRadius: 16,
    minWidth: 380,
    maxWidth: 520,
  },
  dialogTitle: {
    padding: theme.spacing(2, 2.5, 1.5),
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  dialogContent: {
    padding: theme.spacing(2.5),
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  studentListContainer: {
    maxHeight: 320,
    overflow: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    backgroundColor: "#f8fafc",
  },
  studentListItem: {
    borderBottom: "1px solid #e2e8f0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  studentAvatar: {
    backgroundColor: "#2563eb",
    width: 36,
    height: 36,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: "#64748b",
  },
}));

export default function ActivityAnalysis() {
  const classes = useStyles();
  const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};

  const [activityList, setActivityList] = useState({});
  const [consolidatedRows, setConsolidatedRows] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sectionList, setSectionList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [addClassList, setAddClassList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCategory, setDialogCategory] = useState("");

  useEffect(() => {
    getAddClassList();
    return () => {
      setStudentList([]);
      setActivityList({});
      setConsolidatedRows([]);
    };
  }, []);

  const getBucketByKey = (key) => {
    const bucket = activityList && typeof activityList === "object" ? activityList[key] : null;
    return bucket || { activities: [], studentCount: 0, students: [] };
  };

  const getUniqueActivities = (activities) => {
    const seen = new Set();
    return (activities || []).filter((a) => {
      const id = a.activityId ? String(a.activityId) : (a.activityName || "");
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const mergeUniqueStudents = (lists) => {
    const seen = new Set();
    const result = [];
    (lists || []).forEach((list) => {
      (list || []).forEach((s) => {
        const id = s.studentId ? String(s.studentId) : "";
        if (!id || seen.has(id)) return;
        seen.add(id);
        result.push(s);
      });
    });
    return result;
  };

  const mBucket = getBucketByKey("M");
  const wiBucket = getBucketByKey("WI");
  const waBucket = getBucketByKey("WA");
  const pBucket = getBucketByKey("P");

  const strongStudents = mergeUniqueStudents([mBucket.students, wiBucket.students]);
  const developingStudents = waBucket.students || [];
  const needsAttentionStudents = pBucket.students || [];

  const strongCount = strongStudents.length;
  const developingCount = waBucket.studentCount;
  const needsAttentionCount = pBucket.studentCount;

  const selectedClass = addClassList.find((c) => normalizeId(c._id) === normalizeId(classId));
  const selectedClassName = selectedClass ? selectedClass.className : "";
  const selectedSection = sectionList.find((s) => normalizeId(s._id) === normalizeId(sectionId));
  const selectedSectionName = selectedSection ? selectedSection.sectionName : "";

  const getDialogStudents = () => {
    if (dialogCategory === "Strong Areas") return strongStudents;
    if (dialogCategory === "Developing Areas") return developingStudents;
    if (dialogCategory === "Needs Attention") return needsAttentionStudents;
    return [];
  };

  const getUniqueStudentsById = (students) => {
    const seen = new Set();
    return (students || []).filter((s) => {
      const id = s.studentId ? String(s.studentId) : "";
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const dialogStudents = getUniqueStudentsById(getDialogStudents());

  const handleCountClick = (categoryLabel) => {
    setDialogCategory(categoryLabel);
    setDialogOpen(true);
  };

  const onSubmit = () => {
    if (!classId) {
      alert("Please select a class");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    const keys = {
      schooleId: resolveSchoolId(userDetails),
      isFuturePlanning: false,
      studentId: studentId || undefined,
      classId: classId || undefined,
      sectionId: sectionId || userDetails.sectionId || undefined,
    };

    ActivityService.findActivityAnalysisReportsList(keys)
      .then((result) => {
        const buckets = transformActivityData(result);
        setActivityList(buckets);
        setConsolidatedRows(buildConsolidatedData(buckets));
      })
      .catch((err) => {
        console.error(err);
        setActivityList({});
        setConsolidatedRows([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getSectionListByClass = (selectedClassId) => {
    if (!selectedClassId) {
      setSectionList([]);
      setSectionId("");
      return;
    }
    SectionService.findSectionList({
      schooleId: resolveSchoolId(userDetails),
      classId: selectedClassId,
    })
      .then((res) => {
        setSectionList(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        setSectionList([]);
      });
  };

  const getAddClassList = () => {
    AddClassService.getAllAddClass(resolveSchoolId(userDetails))
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setAddClassList(list);

        if (userDetails.role === "TEACHER" && userDetails.classId) {
          const selectedClassId = normalizeId(userDetails.classId);
          const teacherSectionId = normalizeId(userDetails.sectionId);
          setClassId(selectedClassId);
          setSectionId(teacherSectionId);
          getSectionListByClass(selectedClassId);
          getStudentList(selectedClassId, teacherSectionId || undefined);
        } else if (list.length > 0) {
          const defaultClassId = normalizeId(list[0]._id);
          setClassId(defaultClassId);
          setSectionId("");
          getSectionListByClass(defaultClassId);
          getStudentList(defaultClassId);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getStudentList = (selectedClassId, overrideSectionId) => {
    const effectiveSectionId =
      overrideSectionId !== undefined
        ? overrideSectionId
        : sectionId || userDetails.sectionId || undefined;

    const body = { classId: selectedClassId };
    if (effectiveSectionId) {
      body.sectionId = effectiveSectionId;
    }

    StudentService.getAllStudentById(resolveSchoolId(userDetails), body)
      .then((res) => {
        const studentDetails = (res || []).map((student) => ({
          _id: student._id,
          studentName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
        }));
        setStudentList(studentDetails);
      })
      .catch((err) => {
        console.error("Error fetching students:", err);
        setStudentList([]);
      });
  };

  const handleClassChange = (selectedClassId) => {
    setClassId(selectedClassId);
    setSectionId("");
    setStudentId("");
    setActivityList({});
    setConsolidatedRows([]);
    setHasSearched(false);
    getSectionListByClass(selectedClassId);
    getStudentList(selectedClassId);
  };

  const handleSectionChange = (newSectionId) => {
    setSectionId(newSectionId);
    setStudentId("");
    setActivityList({});
    setConsolidatedRows([]);
    setHasSearched(false);
    if (classId) {
      getStudentList(classId, newSectionId || undefined);
    }
  };

  return (
    <Box className={classes.pageWrapper}>
      <Card className={classes.filtersCard} elevation={0}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.filterLabel}>Select Class</Typography>
            <FormControl variant="outlined" size="small" fullWidth className={classes.filterSelect}>
              <Select displayEmpty value={classId} onChange={(e) => handleClassChange(e.target.value)}>
                <MenuItem value=""><em>Select Class</em></MenuItem>
                {addClassList.map(({ _id, className }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{className}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.filterLabel}>Select Section (Optional)</Typography>
            <FormControl variant="outlined" size="small" fullWidth className={classes.filterSelect}>
              <Select
                displayEmpty
                value={sectionId}
                onChange={(e) => handleSectionChange(e.target.value)}
                disabled={!classId || sectionList.length === 0}
              >
                <MenuItem value=""><em>All Sections</em></MenuItem>
                {sectionList.map(({ _id, sectionName }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{sectionName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography className={classes.filterLabel}>Student Name</Typography>
            <FormControl variant="outlined" size="small" fullWidth className={classes.filterSelect}>
              <Select displayEmpty value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <MenuItem value=""><em>All Students</em></MenuItem>
                {studentList.map(({ _id, studentName }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{studentName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              className={classes.searchButton}
              variant="contained"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {hasSearched && !isLoading && (
        <Box className={classes.summaryCardsRow}>
          <Card elevation={0} className={classes.summaryCard} style={{ backgroundColor: "#e6f7ed", borderColor: "#bdebcf" }}>
              <Box className={classes.summaryHead} style={{ color: "#1a7d40" }}>
                <CheckCircleIcon style={{ fontSize: 20 }} />
                Strong Areas
                <span
                  className={classes.summaryCount}
                  style={{ color: "#1a7d40", backgroundColor: "rgba(26,125,64,0.12)" }}
                  onClick={() => handleCountClick("Strong Areas")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCountClick("Strong Areas")}
                >
                  {strongCount}
                </span>
              </Box>
              <ul className={classes.summaryList}>
                {getUniqueActivities(["M", "WI"].flatMap((key) => getBucketByKey(key).activities)).map((activity, index) => (
                  <li key={activity.activityId || index}>{activity.activityName || "Unnamed Activity"}</li>
                ))}
              </ul>
            </Card>

          <Card elevation={0} className={classes.summaryCard} style={{ backgroundColor: "#fff6e5", borderColor: "#ffe2b0" }}>
              <Box className={classes.summaryHead} style={{ color: "#c9740a" }}>
                <WarningIcon style={{ fontSize: 20 }} />
                Developing Areas
                <span
                  className={classes.summaryCount}
                  style={{ color: "#c9740a", backgroundColor: "rgba(201,116,10,0.12)" }}
                  onClick={() => handleCountClick("Developing Areas")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCountClick("Developing Areas")}
                >
                  {developingCount}
                </span>
              </Box>
              <ul className={classes.summaryList}>
                {getUniqueActivities(waBucket.activities).map((activity, index) => (
                  <li key={activity.activityId || `WA-${index}`}>{activity.activityName || "Unnamed Activity"}</li>
                ))}
              </ul>
            </Card>

          <Card elevation={0} className={classes.summaryCard} style={{ backgroundColor: "#fdeaea", borderColor: "#f7c5c5" }}>
              <Box className={classes.summaryHead} style={{ color: "#c62828" }}>
                <ErrorIcon style={{ fontSize: 20 }} />
                Needs Attention
                <span
                  className={classes.summaryCount}
                  style={{ color: "#c62828", backgroundColor: "rgba(198,40,40,0.12)" }}
                  onClick={() => handleCountClick("Needs Attention")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleCountClick("Needs Attention")}
                >
                  {needsAttentionCount}
                </span>
              </Box>
              <ul className={classes.summaryList}>
                {getUniqueActivities(pBucket.activities).map((activity, index) => (
                  <li key={activity.activityId || `P-${index}`}>{activity.activityName || "Unnamed Activity"}</li>
                ))}
              </ul>
            </Card>
        </Box>
      )}

      <Paper className={classes.reportCard} elevation={0}>
        <Box className={classes.reportHeader}>
          <Typography className={classes.reportTitle}>
            Consolidated Activity-wise Report
          </Typography>
          <Button
            variant="contained"
            className={classes.downloadBtn}
            startIcon={<GetAppIcon />}
            disabled={consolidatedRows.length === 0}
            onClick={() => downloadConsolidatedCsv(consolidatedRows)}
          >
            Download CSV
          </Button>
        </Box>

        {isLoading && (
          <Box className={classes.loadingBox}>Loading activity analysis...</Box>
        )}

        {!isLoading && (
          <Box className={classes.tableWrapper}>
            <table className={classes.reportTable}>
              <thead>
                <tr>
                  <th className={classes.reportHeadCell}>Activity</th>
                  <th className={classes.reportHeadCell}>Strong Area Students</th>
                  <th className={classes.reportHeadCell}>Developing Area Students</th>
                  <th className={classes.reportHeadCell}>Needs Attention Students</th>
                </tr>
              </thead>
              <tbody>
                {consolidatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={classes.emptyRow}>
                      {hasSearched
                        ? "No activity data found for the selected filters."
                        : "Select class and click Search to view the consolidated report."}
                    </td>
                  </tr>
                ) : (
                  consolidatedRows.map((row, index) => (
                    <tr key={row.activityId || index}>
                      <td className={`${classes.reportBodyCell} ${classes.activityNameCell}`}>
                        {row.activityName}
                      </td>
                      <StudentCategoryCell students={row.strongStudents} classes={classes} />
                      <StudentCategoryCell students={row.developingStudents} classes={classes} />
                      <StudentCategoryCell students={row.needsAttentionStudents} classes={classes} />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{ className: classes.dialogPaper }}
        aria-labelledby="category-dialog-title"
      >
        <DialogTitle id="category-dialog-title" className={classes.dialogTitle}>
          <Typography variant="h6" component="span" style={{ fontWeight: 700, color: "#0f2d4d" }}>
            {dialogCategory}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.summaryRow}>
            {selectedClassName && (
              <Chip size="small" label={`Class: ${selectedClassName}`} />
            )}
            {selectedSectionName && (
              <Chip size="small" label={`Section: ${selectedSectionName}`} />
            )}
            <Chip
              size="small"
              color="primary"
              label={`${dialogStudents.length} student${dialogStudents.length !== 1 ? "s" : ""}`}
            />
          </Box>

          {dialogCategory === "Strong Areas" && (
            <Box className={classes.statsRow}>
              <Chip size="small" label={`Mastered: ${mBucket.studentCount}`} variant="outlined" />
              <Chip size="small" label={`Works Independently: ${wiBucket.studentCount}`} variant="outlined" />
            </Box>
          )}
          {dialogCategory === "Developing Areas" && (
            <Box className={classes.statsRow}>
              <Chip size="small" label={`Works with Assistance: ${waBucket.studentCount}`} variant="outlined" />
            </Box>
          )}
          {dialogCategory === "Needs Attention" && (
            <Box className={classes.statsRow}>
              <Chip size="small" label={`Presented: ${pBucket.studentCount}`} variant="outlined" />
            </Box>
          )}

          <Typography variant="subtitle2" style={{ marginBottom: 8, color: "#64748b", fontWeight: 700 }}>
            Students
          </Typography>

          {dialogStudents.length > 0 ? (
            <div className={classes.studentListContainer}>
              <List dense disablePadding>
                {dialogStudents.map((student, index) => (
                  <ListItem key={student.studentId || index} className={classes.studentListItem}>
                    <ListItemAvatar>
                      <Avatar className={classes.studentAvatar}>
                        <PersonIcon style={{ fontSize: 20 }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${(student.firstName || "").trim()} ${(student.lastName || "").trim()}`.trim() || "Unnamed Student"}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ) : (
            <div className={classes.emptyState}>
              <Typography variant="body2">No students in this category.</Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setDialogOpen(false)} color="primary" variant="contained" disableElevation>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
